import React, {useEffect, useState} from 'react';
import {CollectionItem} from '../types/CollectionItem';
import collectionsService from '../services/collectionsService';
import {handleError} from '../../../common/utils/errorHandling';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {useTranslation} from 'react-i18next';
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";

interface CollectionItemsTableProps {
  collectionId: string;
}

const CollectionItemsTable: React.FC<CollectionItemsTableProps> = ({collectionId}) => {
  const {t} = useTranslation();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [items, setItems] = useState<CollectionItem[]>([]);

  useEffect(() => {
    fetchItems();
  }, [collectionId]);

  const fetchItems = async (): Promise<void> => {
    await handleError(
      async () => {
        const itemsData = await collectionsService.getCollectionItems(collectionId);
        setItems(itemsData);
      },
      'Error fetching collection items'
    );
  };

  const handleDelete = async (e: React.MouseEvent<HTMLButtonElement>, item: CollectionItem): Promise<void> => {
    e.preventDefault();
    showConfirmDialog({
      message: t('admin.common.messages.confirmRemoveWithName', {name: item.artisticObject.name}),
      accept: async () => {
        await handleError(
          async () => {
            await collectionsService.removeItemFromCollection(collectionId, item.id);
            await fetchItems();
          },
          'Error removing item from collection'
        );
      }
    });
  };

  if (items.length === 0) {
    return (
      <div className="text-gray-500 text-center py-4">
        No items in this collection yet.
      </div>
    );
  }

  return (
    <DataTable
      value={items}
      stripedRows
      showGridlines
      paginator
      rows={10}
    >
      <Column
        header="Artistic Object"
        body={(item: CollectionItem) => (
          <strong>{item.artisticObject.name}</strong>
        )}
      />
      <Column
        header="Place"
        body={(item: CollectionItem) => (
          <span>{item.artisticObject.place.name}</span>
        )}
      />
      <Column
        header="Actions"
        body={(item: CollectionItem) => (
          <Button
            onClick={(e) => handleDelete(e, item)}
            icon="fa fa-trash"
            severity="danger"
            size="small"
            outlined
          />
        )}
      />
    </DataTable>
  );
};

export default CollectionItemsTable;

